import db from "@/database";
import { create } from "@/services/external/therapies-service";
import { createOrUpdate, deleteOne } from "@/services/local/therapies-service";
import router from "@/router";
import { insertFileToQueue } from "@/services/external/therapies-integration-queue-service";
import { TherapyStatusEnum } from "@/domains/therapy/shared/application/therapy-status.enum";
import { SEQUENCE } from "@/domains/therapy/steps/mount-sequence/store/sequence/types";
import {
  awsPutImage,
  getSignedConsentTermPresignedUrl,
  getAdditionalFilePresignedUrl,
} from "@/services/external/aws-service";
import { AttachmentTypeEnum } from "@/domains/therapy/shared/application/attachment-type.enum";
import {
  initDraft,
  backupToS3,
  updateDraft,
  renewPresignedUrl,
  deleteDraft,
  logBackupError,
} from "@/services/external/therapy-draft-service";

const initialState = () => ({
  preTherapyProcedures: "",
  question: {},
  patient: {},
  synced: 0,
  createdAtLocal: new Date(),
  chosenSequence: [],
  step: 1,
  status: TherapyStatusEnum.IN_PROGRESS,
  finishedAt: "",
  signedConsentTerm: null,
  questionsSearched: false,
  additionalFiles: [],
  // Novos campos para draft S3
  draftId: null,
  protocol: null,
  presignedUrl: null,
  presignedUrlExpiresAt: null,
  s3BackupKey: null,
  lastS3BackupAt: null,
});

const state = {
  therapy: initialState(),
};

const mutations = {
  saveQuestion(state, question) {
    // 🔍 DEBUG: Log para verificar o que está sendo salvo
    //console.log("[Mutation] saveQuestion chamado com:", question);
    //console.log(
    //  "[Mutation] historicoDeDoencaAtual:",
    //  question.historicoDeDoencaAtual
    //);

    state.therapy.question = Object.assign(state.therapy.question, question);
  },
  savePatient(state, patient) {
    state.therapy.patient = Object.assign(state.therapy.patient, patient);
  },
  saveSequence(state, chosenSequence) {
    state.therapy.chosenSequence = chosenSequence;
  },
  saveDiagnose(state, diagnose) {
    state.therapy.diagnose = diagnose;
  },
  setId(state, id) {
    state.therapy.localId = id;
  },
  changeStep(state, step) {
    state.therapy.step = step;
  },
  changeTherapyToFinished(state) {
    state.therapy.finishedAt = new Date();
    state.therapy.status = TherapyStatusEnum.NOT_SYNCED;
  },
  setTherapy(state, therapy) {
    // 🔥 FIX: Faz cópia profunda para evitar referências compartilhadas e corrupção de dados
    // Isso resolve o problema de valores inesperados nos campos (ex: HDA com metadados do draft)
    state.therapy = JSON.parse(JSON.stringify(therapy));
  },
  clearTherapy(state) {
    delete state.therapy.localId;
    // 🔥 FIX: Cria novo objeto do initialState() para evitar reutilização de referências
    state.therapy = { ...initialState() };
  },
  setPreTherapyProcedures(state, value) {
    state.therapy.preTherapyProcedures = value;
  },
  setSignedConsentTerm(state, newValue) {
    state.therapy.signedConsentTerm = newValue;
  },
  setQuestionsSearched(state, value) {
    state.therapy.questionsSearched = value;
  },
  setAdditionalFiles(state, value) {
    state.therapy.additionalFiles = value;
  },
  // Novas mutations para draft S3
  updateTherapy(state, updates) {
    Object.assign(state.therapy, updates);
  },
};

const actions = {
  async newTherapy({ commit, dispatch }) {
    commit("clearTherapy");
    await dispatch("clearStates");
    router.push({ name: "therapys.stepper" });
  },
  async saveTherapyOffline({ commit, rootGetters, state }) {
    // 🔥 NOVA LÓGICA: Com sistema de backup S3, IndexedDB NÃO é mais usado como storage principal
    // IndexedDB agora é usado APENAS para terapias offline (sem conexão no momento da criação)

    if (state.therapy.step > 1) {
      // ✅ Se terapia já tem draft na nuvem, backup é feito via S3 (a cada 30s)
      if (state.therapy.draftId) {
        //console.log(
        //  "[Auto-Save] ✅ Terapia tem draft na nuvem (ID:",
        //  state.therapy.draftId,
        //  "), backup automático via S3"
        //);
        return; // NÃO salva no IndexedDB
      }

      // 🔥 Se chegou aqui, significa que está SEM draft na nuvem
      // Isso só acontece se:
      // 1. Usuário está offline no momento
      // 2. Ainda não chegou no step 2 (onde draft é criado)

      // Verifica se está online
      if (navigator.onLine) {
        // Online mas sem draft = aguardando criação no step 2
        //console.log(
        //  "[Auto-Save] ⏳ Aguardando criação de draft na nuvem (step 2)"
        //);
        return; // NÃO salva no IndexedDB
      }

      // 🛡️ FALLBACK OFFLINE: Só salva no IndexedDB se estiver offline
      //console.log(
      //  "[Auto-Save] ⚠️ MODO OFFLINE - Salvando temporariamente no IndexedDB"
      //);

      const diagnosis = rootGetters["diagnosis/diagnose"];
      const sequence = rootGetters["sequence/chosenSequence"];
      commit("saveDiagnose", rootGetters["diagnosis/diagnose"]);
      commit("saveSequence", rootGetters["sequence/chosenSequence"]);

      const updatedTherapyState = {
        ...state.therapy,
        diagnose: {
          ...diagnosis,
          tumoralPhenomena: diagnosis.tumoralPhenomena.map(
            (tumoralPhenomenon) => ({
              ...tumoralPhenomenon,
              relatedPares:
                tumoralPhenomenon.relatedPares?.map((id) => ({
                  id,
                })) || [],
            })
          ),
          potentialSpaces: diagnosis.potentialSpaces.map((potentialSpace) => ({
            ...potentialSpace,
            relatedPares:
              potentialSpace.relatedPares?.map((id) => ({ id })) || [],
          })),
        },
        chosenSequence: sequence,
      };

      const savedId = await createOrUpdate(updatedTherapyState);
      if (!state.therapy.localId) {
        commit("setId", savedId);
      }

      //console.log(
      //  "[Auto-Save] 💾 Salvo no IndexedDB (modo offline) - ID:",
      //  savedId
      //);
    }
  },
  async syncOneTherapy({ dispatch }, therapy) {
    return new Promise(async (resolve, reject) => {
      try {
        therapy = await dispatch("removeImagesOfObject", therapy);
        console.log("therapy", therapy);
        const therapyAttachments = await dispatch("prepareTherapyAttachments", {
          signedConsentTerm: therapy.signedConsentTerm,
          aditionalFiles: therapy.additionalFiles,
        });

        const { data } = await insertFileToQueue({
          therapyAttachments,
          ...therapy,
        });
        await deleteOne(therapy.localId);
        resolve(data);
      } catch (error) {
        reject(error);
      }
    });
  },

  async saveAsJsonLocally({ dispatch }, therapy) {
    return new Promise(async (resolve, reject) => {
      try {
        // Remove images or any unnecessary data from the therapy object
        therapy = await dispatch("removeImagesOfObject", therapy);

        // Prepare attachments if necessary
        const therapyAttachments = await dispatch("prepareTherapyAttachments", {
          signedConsentTerm: therapy.signedConsentTerm,
          aditionalFiles: therapy.additionalFiles,
        });

        // Combine therapy data with attachments
        const therapyData = {
          ...therapy,
          therapyAttachments,
        };

        // Convert the therapy data to a JSON string
        const jsonString = JSON.stringify(therapyData, null, 2);

        // Create a Blob from the JSON string
        const blob = new Blob([jsonString], { type: "application/json" });

        // Create a URL for the Blob
        const url = URL.createObjectURL(blob);

        // Create a temporary anchor element to trigger the download
        const a = document.createElement("a");
        const timestamp = new Date().toISOString().replace(/:/g, "-");
        const fileName = `${therapy.localId || "therapy"}-${timestamp}.json`;
        a.href = url;
        a.download = fileName; // Use a meaningful filename
        document.body.appendChild(a);
        a.click();

        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        resolve("Download initiated successfully.");
      } catch (error) {
        reject(error);
      }
    });
  },

  removeImagesOfObject(context, therapy) {
    // 🔥 CRÍTICO: Faz cópia profunda para NÃO modificar o state do Vuex
    const therapyCopy = JSON.parse(JSON.stringify(therapy));

    // Garante que diagnose existe
    if (!therapyCopy.diagnose) {
      therapyCopy.diagnose = {};
    }

    // Processa tumoralPhenomena apenas se existir
    if (
      therapyCopy.diagnose.tumoralPhenomena &&
      Array.isArray(therapyCopy.diagnose.tumoralPhenomena)
    ) {
      therapyCopy.diagnose.tumoralPhenomena =
        therapyCopy.diagnose.tumoralPhenomena.map((tumoralPhenomenon) => {
          if (tumoralPhenomenon?.diagnoseResolutionZone?.resolutionZone) {
            delete tumoralPhenomenon.diagnoseResolutionZone.resolutionZone
              .trackingPoint;
            delete tumoralPhenomenon.diagnoseResolutionZone.resolutionZone
              .impactionPoint;
          }
          return tumoralPhenomenon;
        });
    }

    // Processa vascularPhenomena apenas se existir
    if (
      therapyCopy.diagnose.vascularPhenomena &&
      Array.isArray(therapyCopy.diagnose.vascularPhenomena)
    ) {
      therapyCopy.diagnose.vascularPhenomena =
        therapyCopy.diagnose.vascularPhenomena.map((vascularPhenomenon) => {
          if (vascularPhenomenon?.diagnoseResolutionZone?.resolutionZone) {
            delete vascularPhenomenon.diagnoseResolutionZone.resolutionZone
              .trackingPoint;
            delete vascularPhenomenon.diagnoseResolutionZone.resolutionZone
              .impactionPoint;
          }
          return vascularPhenomenon;
        });
    }

    // Processa potentialSpaces apenas se existir
    if (
      therapyCopy.diagnose.potentialSpaces &&
      Array.isArray(therapyCopy.diagnose.potentialSpaces)
    ) {
      therapyCopy.diagnose.potentialSpaces =
        therapyCopy.diagnose.potentialSpaces.map((potentialSpace) => {
          if (potentialSpace?.diagnoseResolutionZone?.resolutionZone) {
            delete potentialSpace.diagnoseResolutionZone.resolutionZone
              .trackingPoint;
            delete potentialSpace.diagnoseResolutionZone.resolutionZone
              .impactionPoint;
          }
          return potentialSpace;
        });
    }

    // Processa flowTypes apenas se existir
    if (
      therapyCopy.diagnose.flowTypes &&
      Array.isArray(therapyCopy.diagnose.flowTypes)
    ) {
      therapyCopy.diagnose.flowTypes = therapyCopy.diagnose.flowTypes.map(
        (diagnoseFlowType) => {
          if (diagnoseFlowType?.diagnoseResolutionZone?.resolutionZone) {
            delete diagnoseFlowType.diagnoseResolutionZone.resolutionZone
              .trackingPoint;
            delete diagnoseFlowType.diagnoseResolutionZone.resolutionZone
              .impactionPoint;
          }
          return diagnoseFlowType;
        }
      );
    }

    // Processa chakras apenas se existir
    if (
      therapyCopy.diagnose.chakras &&
      Array.isArray(therapyCopy.diagnose.chakras)
    ) {
      therapyCopy.diagnose.chakras = therapyCopy.diagnose.chakras.map(
        (diagnoseChakra) => {
          if (diagnoseChakra?.chakra?.image) {
            delete diagnoseChakra.chakra.image;
          }
          return diagnoseChakra;
        }
      );
    }

    return therapyCopy;
  },

  async finish({ dispatch, commit, state }) {
    console.log("[Finish] Iniciando finalização da terapia...");

    // Se tem draft na nuvem OU está online: usa fluxo completo com envio para fila
    // finishTherapyWithDraft já trata draftId === null internamente (não chama deleteDraft)
    if (state.therapy.draftId || navigator.onLine) {
      //console.log("[Finish] Online ou com draft, usando finishTherapyWithDraft");
      await dispatch("finishTherapyWithDraft");
      //console.log("[Finish] ✅ Terapia finalizada com sucesso");
    } else {
      // Offline E sem draft: salva localmente para sincronização posterior
      //console.log("[Finish] Offline sem draft, salvando localmente");
      commit("changeTherapyToFinished");
      await dispatch("saveTherapyOffline");
      //console.log("[Finish] ✅ Terapia salva localmente para sincronização posterior");
    }
  },

  async syncAllTherapys() {
    const therapys = await db.therapy.toArray();
    await create(therapys);
  },

  changeStep({ commit, dispatch }, step) {
    commit("changeStep", step);
  },

  loadTherapy({ commit, dispatch }, therapy) {
    try {
      const therapyToLoad = { ...therapy };

      // Drafts vindos do S3 devem sempre iniciar no primeiro passo para evitar telas incoerentes
      if (therapyToLoad.isCloudDraft || therapyToLoad.s3BackupKey) {
        therapyToLoad.step = 1;
        therapyToLoad.currentStep = 1;
      }

      commit("setTherapy", therapyToLoad);
      commit(
        SEQUENCE.MUTATIONS.SET_SELECTED_ITEMS,
        therapyToLoad.chosenSequence,
        {
          root: true,
        }
      );
      commit("diagnosis/setDiagnose", therapyToLoad.diagnose, { root: true });

      // 🔥 FIX: Restaura fenômenos tumorais no submódulo (diagnosis.tumoralPhenomena.added)
      // necessário pois TumoralPhenomenaSection lê diretamente de state.diagnosis.tumoralPhenomena.added
      commit("diagnosis/tumoralPhenomena/clear", null, { root: true });
      if (therapyToLoad.diagnose?.tumoralPhenomena?.length > 0) {
        therapyToLoad.diagnose.tumoralPhenomena.forEach((tp) => {
          commit("diagnosis/tumoralPhenomena/addToDiagnose", tp, {
            root: true,
          });
        });
      }

      dispatch(SEQUENCE.ACTIONS.MOUNT_SEQUENCE, {}, { root: true });
    } catch (error) {
      console.error("[Load Therapy] Erro ao carregar terapia:", error);
      throw error;
    }
  },

  async deleteTherapy({ commit }, { index, therapy, status, protocol }) {
    console.log("[Delete] Iniciando exclusão - therapy:", {
      isCloudDraft: therapy?.isCloudDraft,
      draftId: therapy?.draftId,
      localId: therapy?.localId,
      protocol: therapy?.protocol || protocol,
      status,
    });

    // 🔥 CASO 1: Draft da nuvem (S3) - Deletar do backend
    if (therapy?.isCloudDraft && therapy?.draftId) {
      await deleteDraft(therapy.draftId);
      //console.log("[Delete] ✅ Draft da nuvem deletado:", therapy.draftId);

      // 🔥 CRÍTICO: Remove TODAS as cópias do IndexedDB que referenciem este draft
      // Isso resolve o problema de terapias que foram salvas antes da refatoração
      try {
        // 1. Tenta deletar pelo localId (se houver)
        if (therapy?.localId) {
          await db.therapy.delete(therapy.localId);
          //console.log(
          //  "[Delete] ✅ Terapia removida do IndexedDB (por localId):",
          //  therapy.localId
          //);
        }

        // 2. Busca e deleta qualquer terapia no IndexedDB que tenha este draftId
        const therapiesWithDraftId = await db.therapy
          .filter((t) => t.draftId === therapy.draftId)
          .toArray();

        if (therapiesWithDraftId.length > 0) {
          //console.log(
          //  `[Delete] 🧹 Encontradas ${therapiesWithDraftId.length} terapia(s) órfã(s) no IndexedDB com draftId:`,
          //  therapy.draftId
          //);

          for (const orphanTherapy of therapiesWithDraftId) {
            await db.therapy.delete(orphanTherapy.localId);
            //console.log(
            //  "[Delete] ✅ Terapia órfã removida:",
            //  orphanTherapy.localId
            //);
          }
        }
      } catch (error) {
        console.warn(
          "[Delete] ⚠️ Erro ao limpar IndexedDB (pode não existir):",
          error
        );
      }
      return;
    }

    // 🔥 CASO 2: Terapia sincronizada no backend - Exclusão lógica via API
    if (status === TherapyStatusEnum.SYNCED && (protocol || therapy.protocol)) {
      const { softDelete } = await import(
        "@/services/external/therapies-service"
      );
      const protocolToDelete = protocol || therapy.protocol;
      await softDelete(protocolToDelete);
      //console.log(
      //  "[Delete] ✅ Terapia sincronizada deletada (soft delete):",
      //  protocolToDelete
      //);
      return;
    }

    // 🔥 CASO 3: Terapia apenas local (IndexedDB) - offline ou não finalizada
    if (therapy?.localId) {
      await db.therapy.delete(therapy.localId);
      //console.log(
      //  "[Delete] ✅ Terapia local (IndexedDB) deletada:",
      //  therapy.localId
      //);
      return;
    }

    //console.warn(
    //  "[Delete] ⚠️ Nenhuma condição de exclusão atendida - terapia pode não ter sido deletada"
    //);
  },

  async clearStates({ commit, dispatch }) {
    commit("diagnosis/clearDiagnose", null, { root: true });
    commit("diagnosis/tumoralPhenomena/clear", null, { root: true });
    commit("tumoralPhenomena/clear", null, { root: true });
    commit(SEQUENCE.MUTATIONS.CLEAR_SEQUENCE, null, { root: true });
    dispatch("pares/clearState", null, { root: true });
  },

  /**
   * Inicia novo draft no backend e recebe presigned URL
   */
  async initTherapyDraft({ commit, state }) {
    try {
      //console.log("[Draft] Iniciando novo draft...");

      const draft = await initDraft();

      // Atualiza state com informações do draft
      commit("updateTherapy", {
        draftId: draft.draftId,
        protocol: draft.protocol,
        presignedUrl: draft.presignedUrl,
        presignedUrlExpiresAt: draft.presignedUrlExpiresAt,
        s3BackupKey: draft.s3BackupKey,
      });

      // Salva no IndexedDB
      await createOrUpdate(state.therapy);

      //console.log("[Draft] Draft criado:", draft);
      return draft;
    } catch (error) {
      console.error("[Draft] Erro ao criar draft:", error);
      throw error;
    }
  },

  /**
   * Faz backup periódico no S3 via presigned URL
   */
  async backupTherapyToS3(
    { state, commit, rootState, rootGetters },
    { retryCount = 0 } = {}
  ) {
    try {
      // Verifica se já tem draft criado
      if (!state.therapy.draftId || !state.therapy.presignedUrl) {
        console.warn(
          "[S3 Backup] Draft ainda não inicializado, pulando backup"
        );
        return;
      }

      // Verifica se presigned URL está válida (> 6 horas restante)
      const expiresAt = new Date(state.therapy.presignedUrlExpiresAt);
      const sixHoursFromNow = Date.now() + 6 * 60 * 60 * 1000;

      if (expiresAt < sixHoursFromNow) {
        //console.log("[S3 Backup] Presigned URL expirando, renovando...");
        const { presignedUrl } = await renewPresignedUrl(state.therapy.draftId);
        commit("updateTherapy", {
          presignedUrl,
          presignedUrlExpiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
        });
      }

      //console.log("[S3 Backup] Enviando backup para S3...");

      // 🔥 FIX: Usa getters que sempre retornam o estado ao vivo:
      // - diagnosis/diagnose inclui tumoralPhenomena do submódulo
      // - sequence/chosenSequence = sequence.selectedItems atual
      //   (state.therapy.chosenSequence só é atualizado após MOUNT_SEQUENCE;
      //    se o usuário está no step 3 sem clicar em Avançar, seria desatualizado)
      const therapyWithDiagnose = {
        ...state.therapy,
        chosenSequence:
          rootGetters["sequence/chosenSequence"] ||
          state.therapy.chosenSequence,
        diagnose: rootGetters["diagnosis/diagnose"] || null,
      };

      console.log("[S3 Backup] 📦 Payload completo:", therapyWithDiagnose);

      await backupToS3(state.therapy.presignedUrl, therapyWithDiagnose);

      const now = new Date();
      commit("updateTherapy", { lastS3BackupAt: now });

      // Atualiza metadados no backend (incluindo ID, nome e e-mail do paciente)
      // patientName e patientEmail garantem identificação mesmo para pacientes
      // novos que ainda não possuem conta (patientId seria null nesses casos).
      const patientId = state.therapy.patient?.id || null;
      const patientName = state.therapy.patient?.name || null;
      const patientEmail =
        state.therapy.patient?.email || state.therapy.patient?.mail || null;

      await updateDraft(state.therapy.draftId, {
        currentStep: state.therapy.step,
        lastBackupAt: now,
        patientId: patientId,
        patientName: patientName,
        patientEmail: patientEmail,
      });

      //console.log("[S3 Backup] ✅ Backup enviado com sucesso");
    } catch (error) {
      console.error("[S3 Backup] Erro ao fazer backup:", error);

      // Calcula tamanho do payload para log
      const therapyWithDiagnose = {
        ...state.therapy,
        chosenSequence:
          rootGetters["sequence/chosenSequence"] ||
          state.therapy.chosenSequence,
        diagnose: rootGetters["diagnosis/diagnose"] || null,
      };
      const payloadSize = new Blob([JSON.stringify(therapyWithDiagnose)]).size;

      // Envia log de erro para o backend
      await logBackupError({
        draftId: state.therapy.draftId,
        errorType: error.name || "UnknownError",
        errorMessage: error.message || "Erro desconhecido ao fazer backup S3",
        httpStatus: error.response?.status,
        isNetworkError: !error.response,
        payloadSize,
        retryCount,
        additionalInfo: {
          step: state.therapy.step,
          protocol: state.therapy.protocol,
          hasPresignedUrl: !!state.therapy.presignedUrl,
          presignedUrlExpiresAt: state.therapy.presignedUrlExpiresAt,
          errorCode: error.code,
        },
      });

      // Retry automático com exponential backoff (até 3 tentativas)
      if (retryCount < 3) {
        const delay = Math.pow(2, retryCount) * 1000; // 1s, 2s, 4s
        console.log(
          `[S3 Backup] Tentando novamente em ${delay}ms (tentativa ${
            retryCount + 1
          }/3)...`
        );

        await new Promise((resolve) => setTimeout(resolve, delay));

        // Retry recursivo
        return await this.dispatch("therapy/backupTherapyToS3", {
          retryCount: retryCount + 1,
        });
      }

      // Falhou após 3 tentativas
      console.error("[S3 Backup] ❌ Falhou após 3 tentativas");

      // Não lança erro para não quebrar o fluxo (offline-first continua)
      // Tentará novamente no próximo ciclo do timer
    }
  },

  /**
   * Finaliza terapia: envia para fila e limpa draft
   */
  async finishTherapyWithDraft({
    state,
    dispatch,
    commit,
    rootState,
    rootGetters,
  }) {
    try {
      //console.log("[Therapy] Finalizando terapia com draft...");

      // 1. Marca como finalizada usando mutation
      commit("changeTherapyToFinished");

      // 2. Envia para fila de processamento (fluxo existente)
      // Usa getters que sempre refletem estado ao vivo (inclui tumoralPhenomena e chosenSequence atual)
      const therapyWithDiagnose = {
        ...state.therapy,
        chosenSequence:
          rootGetters["sequence/chosenSequence"] ||
          state.therapy.chosenSequence,
        diagnose: rootGetters["diagnosis/diagnose"] || null,
      };

      // removeImagesOfObject já faz deep clone internamente
      const therapy = await dispatch(
        "removeImagesOfObject",
        therapyWithDiagnose
      );
      const therapyAttachments = await dispatch("prepareTherapyAttachments", {
        signedConsentTerm: state.therapy.signedConsentTerm,
        aditionalFiles: state.therapy.additionalFiles,
      });

      await insertFileToQueue({
        therapyAttachments,
        ...therapy,
      });

      //console.log("[Therapy] ✅ Terapia enviada para fila de processamento");      // 3. Deleta draft (backend + S3)
      if (state.therapy.draftId) {
        await deleteDraft(state.therapy.draftId);
        //console.log("[Draft] ✅ Draft deletado do backend e S3");
      }

      // 4. NÃO salva no IndexedDB (terapia já está na nuvem)
      // Remove do IndexedDB se existir localmente
      if (state.therapy.localId) {
        await deleteOne(state.therapy.localId);
        //console.log("[Therapy] ✅ Terapia removida do IndexedDB local");
      }

      //console.log(
      //  "[Therapy] ✅ Terapia finalizada e enviada para processamento"
      //);

      // 5. Limpa states
      await dispatch("clearStates");

      return true;
    } catch (error) {
      console.error("[Therapy] ❌ Erro ao finalizar terapia:", error);
      throw error;
    }
  },

  /**
   * Faz upload imediato de arquivos adicionais ao S3 e substitui File objects
   * por metadados com URL no state (para que o backup S3 possa serializá-los).
   */
  async uploadAdditionalFiles({ commit, state }, newFilesArray) {
    // 🔥 Atualiza state imediatamente (inclui File objects temporariamente para feedback visual)
    commit("setAdditionalFiles", newFilesArray);

    // Identifica quais itens são File/Blob novos (ainda não uploaded)
    const filesToUpload = newFilesArray.filter(
      (f) => f && (f instanceof File || f instanceof Blob)
    );

    if (filesToUpload.length === 0) return; // Apenas remoções, nada a fazer

    // Faz upload de cada arquivo e substitui no state
    for (const file of filesToUpload) {
      try {
        const {
          data: { key, url, resourceUrl },
        } = await getAdditionalFilePresignedUrl(file.type);
        await awsPutImage({
          url,
          key,
          contentType: file.type,
          fileImage: file,
        });

        const uploadedMetadata = {
          isAlreadyUploaded: true,
          resourceUrl,
          originalFileName: file.name,
          name: file.name,
          type: file.type,
          contentType: file.type,
        };

        // Substitui o File object pelo metadado no state
        const current = [...state.therapy.additionalFiles];
        const idx = current.findIndex((f) => f === file);
        if (idx !== -1) {
          current[idx] = uploadedMetadata;
          commit("setAdditionalFiles", current);
        }
      } catch (error) {
        console.error("[Upload Adicional] Erro ao fazer upload:", error);
        // Mantém o File object no state — falhará graciosamente na finalização
      }
    }
  },

  /**
   * Faz upload imediato do termo de consentimento assinado ao S3.
   */
  async uploadSignedConsentTerm({ commit }, file) {
    if (!file) {
      commit("setSignedConsentTerm", null);
      return;
    }

    // Se já é metadado uploaded, apenas salva
    if (file.isAlreadyUploaded) {
      commit("setSignedConsentTerm", file);
      return;
    }

    // 🔥 Salva imediatamente o File no state para feedback visual
    commit("setSignedConsentTerm", file);

    try {
      const {
        data: { key, url, resourceUrl },
      } = await getSignedConsentTermPresignedUrl(file.type);
      await awsPutImage({ url, key, contentType: file.type, fileImage: file });

      const uploadedMetadata = {
        isAlreadyUploaded: true,
        resourceUrl,
        originalFileName: file.name,
        name: file.name,
        type: file.type,
        contentType: file.type,
      };

      commit("setSignedConsentTerm", uploadedMetadata);
    } catch (error) {
      console.error("[Upload Consentimento] Erro ao fazer upload:", error);
      // Mantém o File object no state — falhará graciosamente na finalização
    }
  },

  async prepareTherapyAttachments({}, { signedConsentTerm, aditionalFiles }) {
    let aditionalFilesUrl = [];
    if (aditionalFiles?.length > 0) {
      aditionalFilesUrl = await Promise.all(
        aditionalFiles.map(async (file) => {
          if (!file) {
            return;
          }
          // 🔥 FIX: Se o arquivo já foi uploaded (eager upload), usa a URL diretamente
          if (file.isAlreadyUploaded && file.resourceUrl) {
            return {
              type: AttachmentTypeEnum.ADITIONAL_FILE,
              url: file.resourceUrl,
              originalFileName: file.originalFileName || file.name,
            };
          }
          // Fallback: File object ainda não uploaded (upload normal)
          const {
            data: { key, url, resourceUrl },
          } = await getAdditionalFilePresignedUrl(file.type);
          await awsPutImage({
            url,
            key,
            contentType: file.type,
            fileImage: file,
          });
          return {
            type: AttachmentTypeEnum.ADITIONAL_FILE,
            url: resourceUrl,
            originalFileName: file.name,
          };
        })
      );
    }

    let signedConsentTermUrl = [];
    if (signedConsentTerm) {
      // 🔥 FIX: Se o termo já foi uploaded (eager upload), usa a URL diretamente
      if (
        signedConsentTerm.isAlreadyUploaded &&
        signedConsentTerm.resourceUrl
      ) {
        signedConsentTermUrl = [
          {
            type: AttachmentTypeEnum.SIGNED_CONSENT_TERM,
            url: signedConsentTerm.resourceUrl,
            originalFileName:
              signedConsentTerm.originalFileName || signedConsentTerm.name,
          },
        ];
      } else if (
        signedConsentTerm instanceof Blob ||
        signedConsentTerm instanceof File
      ) {
        // Fallback: File object ainda não uploaded
        const {
          data: { key, url, resourceUrl },
        } = await getSignedConsentTermPresignedUrl(signedConsentTerm.type);
        await awsPutImage({
          url,
          key,
          contentType: signedConsentTerm.type,
          fileImage: signedConsentTerm,
        });
        signedConsentTermUrl = [
          {
            type: AttachmentTypeEnum.SIGNED_CONSENT_TERM,
            url: resourceUrl,
            originalFileName: signedConsentTerm.name,
          },
        ];
      }
    }

    return [...signedConsentTermUrl, ...aditionalFilesUrl];
  },
};

const getters = {
  patient(state) {
    return state.therapy.patient;
  },
  chosenSequence(state) {
    return state.therapy.chosenSequence;
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
